import { CheckIcon, CopyIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export enum CopyableValueIconPosition {
  Left = "left",
  Right = "right",
}

export const CopyableValue = ({
  value,
  iconPosition = CopyableValueIconPosition.Right,
}: {
  value: string;
  iconPosition?: CopyableValueIconPosition;
}) => {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error(t("ErrorCopyingToClipboard"));
    }
  };

  const icon = copied ? (
    <CheckIcon className="text-primary size-3 shrink-0" />
  ) : (
    <CopyIcon className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={t("ClickToCopy", { value })}
            className="group hover:text-primary flex cursor-pointer items-center gap-1 text-left transition-colors"
          >
            {iconPosition === CopyableValueIconPosition.Left && icon}
            <span>{value}</span>
            {iconPosition === CopyableValueIconPosition.Right && icon}
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("ClickToCopy", { value })}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
